// ⚙️ 상태 변경 및 알림톡 관리 훅
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Application } from '../../../types/application';
import type { ActualApplicationStatus, StatusManagementState } from '../types/applicationStickyHeader.types';
import { STATUS_TO_ALERT_TEMPLATE, API_BASE_URL } from '../types/alertTemplates.constants';
import { biztalkTemplates } from '../../../../../../verify-functions/src/utils/biztalkTemplates';
import type { TemplateType } from '../../orderDetail/types';
import { formatDate, formatDeviceNames } from '../../orderDetail/utils';

export interface UseStatusManagementReturn extends StatusManagementState {
  canSendAlert: boolean;
  setTempStatus: (status: ActualApplicationStatus) => void;
  setIsStatusChanged: (changed: boolean) => void;
  setIsStatusSaving: (saving: boolean) => void;
  setSendAlert: (send: boolean) => void;
  setAlertPreviewOpen: (open: boolean) => void;
  setAlertPreviewMessage: (message: string) => void;
  perDevicePreviews?: Array<{
    deviceId: string;
    deviceModel: string;
    grade?: string | null;
    priceStr: string;
    event: string;
    message: string;
    errors: string[];
    status: string;
  }>;
  perDevicePreviewsSelected?: Array<{
    deviceId: string;
    deviceModel: string;
    grade?: string | null;
    priceStr: string;
    event: string;
    message: string;
    errors: string[];
    status: string;
  }>;
  selectedDeviceIds: string[];
  toggleDeviceSelection: (deviceId: string) => void;
  toggleAllSelection: (checked: boolean) => void;
  handleStatusChange: (newStatus: ActualApplicationStatus) => void;
  handleStatusSave: () => Promise<void>;
  handleStatusReset: () => void;
  handleAlertCheckChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePreviewOpen: () => void;
  handlePreviewClose: () => void;
  executeStatusSave: (withAlert?: boolean) => Promise<void>;
  // 🆕 정보입력요청 관련
  isInfoRequestMode: boolean;
  handleInfoRequestSelect: () => void;
  executeInfoRequest: () => Promise<void>;
}

/**
 * 애플리케이션 상태 변경 및 알림톡 전송 기능을 관리하는 훅
 * - 상태 변경 관리
 * - 알림톡 미리보기 생성
 * - 알림톡 전송
 * - 상태 저장 및 리셋
 */
export const useStatusManagement = (
  application: Application,
  onStatusChange?: (status: string) => Promise<void>,
  onAfterSave?: () => void
): UseStatusManagementReturn => {
  // 🎯 상태 관리
  const [tempStatus, setTempStatus] = useState<ActualApplicationStatus>(application.status as ActualApplicationStatus);
  const [isStatusChanged, setIsStatusChanged] = useState(false);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [sendAlert, setSendAlert] = useState(true);
  const [alertPreviewOpen, setAlertPreviewOpen] = useState(false);
  const [alertPreviewMessage, setAlertPreviewMessage] = useState('');
  
  // 🆕 정보입력요청 모드 (상태 변경 없이 알림톡만 발송)
  const [isInfoRequestMode, setIsInfoRequestMode] = useState(false);
  // 가격안내 대상: 검수가격이 설정된 기기만 (0 포함)
  const eligibleDeviceIds = useMemo(
    () => (application.devices || [])
      .filter((d: any) => d.price_offer !== null && d.price_offer !== undefined)
      .map((d: any) => d.id),
    [application]
  );

  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>(() => eligibleDeviceIds);
  const perDevicePreviews = useMemo(() => {
    if (!application || tempStatus !== '가격안내') return [] as any[];
    // 상태 변경 직전에는 기기 status가 아직 '가격안내'가 아닐 수 있으므로, 가격이 입력된 기기를 대상으로 미리보기 생성
    const items = (application.devices || []).filter((d: any) => d.price_offer !== null && d.price_offer !== undefined);
    return items.map((d: any) => {
      // 🆕 단일 '가격안내' 템플릿 사용
      const event = '가격안내';
      const t = biztalkTemplates[event];
      const priceStr = (d.price_offer || 0).toLocaleString();
      // 🆕 차감사유 라인: 값이 있을 때만 표시
      const deductionReasonLine = d.deduction_reason?.trim() 
        ? `차감사유 : ${d.deduction_reason.trim()}` 
        : '';
      const msg = t?.getMessage({
        userName: application.user_name,
        deviceName: d.deviceModel,
        applyDate: formatDate(application.created_at),
        price: priceStr,
        application_id: application.id,
        device_id: d.id,
        // 🆕 가격안내 단일 템플릿용 파라미터
        grade: d.grade || '-',
        deductionReasonLine,
        applicationNumber: application.application_number?.toString() || application.id,
      }) || '';
      const errors: string[] = [];
      if (!t) errors.push('템플릿 누락');
      if (t?.buttons) {
        t.buttons.forEach(btn => {
          if (btn.type === 'WL') {
            if (!btn.url_mobile || !btn.url_pc) errors.push(`버튼 URL 누락: ${btn.name}`);
          }
        });
      } else {
        errors.push('버튼 정보 누락');
      }
      if (d.price_offer === null || d.price_offer === undefined) errors.push('검수금액 누락');
      return { deviceId: d.id, deviceModel: d.deviceModel, grade: d.grade, priceStr, event, message: msg, errors, status: d.status };
    });
  }, [application, tempStatus]);

  const perDevicePreviewsSelected = useMemo(() => {
    if (tempStatus !== '가격안내') return [] as any[];
    const set = new Set(selectedDeviceIds);
    return (perDevicePreviews || []).filter((p: any) => set.has(p.deviceId));
  }, [perDevicePreviews, selectedDeviceIds, tempStatus]);

  // 🔔 현재 선택된 상태에 대한 알림톡 전송 가능 여부
  const canSendAlert = isStatusChanged && STATUS_TO_ALERT_TEMPLATE[tempStatus] !== null;

  // 🔄 application 변경 시 tempStatus 리셋
  useEffect(() => {
    setTempStatus(application.status as ActualApplicationStatus);
    setIsStatusChanged(false);
    setSendAlert(true);
    // 신청서가 바뀌면 가격안내 대상도 재계산
    setSelectedDeviceIds(eligibleDeviceIds);
  }, [application.status, eligibleDeviceIds]);

  // 🔔 알림톡 미리보기 메시지 생성
  const generateAlertPreview = useCallback((status: ActualApplicationStatus) => {
    const templateType = STATUS_TO_ALERT_TEMPLATE[status];
    if (!templateType) return '';

    const template = biztalkTemplates[templateType];
    if (!template) return '';

    const baseParams = {
      userName: application.user_name,
      deviceName: formatDeviceNames(application.devices),
      applyDate: formatDate(application.created_at),
    };

    // 상태별 특수 처리
    switch (status) {
      case '가격안내':
        const priceParams = {
          ...baseParams,
          price: application.price_offer?.toLocaleString() || '0',
          application_id: application.id,
        };
        return template.getMessage(priceParams);
      
      case '완료':
        const completeParams = {
          ...baseParams,
          finalPrice: application.final_price?.toLocaleString() || '0',
        };
        return template.getMessage(completeParams);
      
      case '반송':
        const returnParams = {
          ...baseParams,
          returnTrackingNumber: '처리 중', // 실제로는 반송장번호 입력 필요
        };
        return template.getMessage(returnParams);
      
      default:
        return template.getMessage(baseParams);
    }
  }, [application]);

  // 🔔 상태 변경 시 알림톡 옵션 업데이트
  useEffect(() => {
    if (isStatusChanged && tempStatus !== application.status) {
      const hasTemplate = STATUS_TO_ALERT_TEMPLATE[tempStatus] !== null;
      if (hasTemplate) {
        const preview = generateAlertPreview(tempStatus);
        // 가격안내인 경우 통합 미리보기 대신 per-device가 다이얼로그에서 사용되므로 안내 문구로 대체
        if (tempStatus === '가격안내') {
          setAlertPreviewMessage('[가격안내] 디바이스별 미리보기가 표시됩니다.');
        } else {
          setAlertPreviewMessage(preview);
        }
        setSendAlert(true);
      } else {
        setAlertPreviewMessage('');
        setSendAlert(false);
      }
    }
  }, [tempStatus, isStatusChanged, generateAlertPreview, application.status]);

  // ⚙️ 상태 변경 핸들러
  const handleStatusChange = useCallback((newStatus: ActualApplicationStatus) => {
    setTempStatus(newStatus);
    setIsStatusChanged(newStatus !== application.status);
    // 가격안내로 변경될 때 기본 전체 선택으로 초기화
    if (newStatus === '가격안내') {
      // 검수가격이 설정된 기기만 선택
      setSelectedDeviceIds(eligibleDeviceIds);
    }
  }, [application.status, eligibleDeviceIds]);

  // 📨 알림톡 전송 함수
  const sendAlertMessage = useCallback(async (templateType: TemplateType) => {
    try {
      const isPriceNotice = templateType === '가격안내';

      if (isPriceNotice) {
        // 가격안내는 품목별 per-device API 호출로 처리 (대상: 검수가격 설정된 기기만)
        const { sendAlimtalkPriceNotice } = await import('../../../../../utils/alerts');
        const eligibleSet = new Set(eligibleDeviceIds);
        const filtered = selectedDeviceIds.filter(id => eligibleSet.has(id));
        if (filtered.length === 0) throw new Error('가격안내 대상 기기가 없습니다. 검수가격을 입력해 주세요.');
        const result = await sendAlimtalkPriceNotice(application.id, false, filtered);
        console.log('✅ per-device 가격안내 전송 성공:', result);
        return true;
      }

      // 비-가격안내: 기존 템플릿 기반 전송
      const template = biztalkTemplates[templateType];
      if (!template) throw new Error('템플릿을 찾을 수 없습니다.');

      const deviceName = formatDeviceNames(application.devices);
      const applyDate = formatDate(application.created_at);

      const payload: any = {
        application_id: application.id,
        user_name: application.user_name,
        phone_number: application.phone_number,
        device_name: deviceName,
        apply_date: applyDate,
        event: templateType,
        title: template.getTitle?.({
          userName: application.user_name,
          deviceName: deviceName,
          applyDate: applyDate,
        }) || '',
        link: template.link || null,
      };

      // 상태별 특수 파라미터 추가 (비-가격안내)
      switch (tempStatus) {
        case '완료':
          payload.finalPrice = application.final_price?.toLocaleString() || '0';
          break;
        case '반송':
          payload.returnTrackingNumber = '처리 중';
          break;
      }

      console.log('📤 알림톡 전송 파라미터 (non-price):', payload);

      const response = await fetch(`${API_BASE_URL}/api/alerts/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: 알림톡 전송 실패`);
      }

      const result = await response.json();
      console.log('✅ 알림톡 전송 성공 (non-price):', result);
      return true;
    } catch (error) {
      console.error('❌ 알림톡 전송 실패:', error);
      throw error;
    }
  }, [application, tempStatus, selectedDeviceIds]);

  // 💾 상태 저장 핸들러 (알림톡 미리보기 포함)
  const handleStatusSave = useCallback(async () => {
    if (!onStatusChange || !isStatusChanged) return;

    // 🔔 알림톡 전송이 체크되어 있고 메시지가 있으면 미리보기 다이얼로그 먼저 표시
    if (sendAlert && alertPreviewMessage) {
      setAlertPreviewOpen(true);
      return;
    }

    // 🔄 알림톡 없이 상태만 변경하는 경우
    await executeStatusSave(false);
  }, [onStatusChange, isStatusChanged, sendAlert, alertPreviewMessage]);

  // 💾 실제 상태 저장 실행 함수
  const executeStatusSave = useCallback(async (withAlert: boolean = false) => {
    if (!onStatusChange || !isStatusChanged) return;

    setIsStatusSaving(true);
    try {
      // 1. 상태 변경 실행
      if (tempStatus === '가격안내') {
        // 선택된 기기만 가격안내로 변경 (배치 업데이트 → 실패 시 단건 업데이트 폴백)
        const eligibleSet = new Set(eligibleDeviceIds);
        const selectedEligibleIds = selectedDeviceIds.filter(id => eligibleSet.has(id));
        if (selectedEligibleIds.length === 0) throw new Error('가격안내로 변경할 대상 기기가 없습니다.');
        const updatesPayload = selectedEligibleIds.map(id => ({ device_id: id, updates: { status: '가격안내' as any } }));
        let batchOk = false;
        try {
          const response = await fetch(`${API_BASE_URL}/api/applications/${application.id}/devices/batch`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ device_updates: updatesPayload })
          });
          if (response.ok) {
            batchOk = true;
          } else {
            // 404 또는 기타 오류 시 폴백
            console.warn('배치 업데이트 실패, 단건 업데이트로 폴백합니다.', response.status);
          }
        } catch (e) {
          console.warn('배치 업데이트 예외, 단건 업데이트로 폴백합니다.', e);
        }

        if (!batchOk) {
          // 단건 업데이트 폴백
          for (const id of selectedEligibleIds) {
            const res = await fetch(`${API_BASE_URL}/api/applications/${application.id}/devices/${id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ status: '가격안내' })
            });
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              throw new Error(data.error || data.message || `HTTP ${res.status}`);
            }
          }
        }

        // ✅ 전체 선택인 경우에만 신청서 상태를 '가격안내'로 승격
        const totalDevices = (application.devices || []).length;
        if (selectedEligibleIds.length === totalDevices && totalDevices > 0) {
          await onStatusChange('가격안내');
          // 전체 선택이므로 tempStatus 유지(가격안내)
        } else {
          // 일부만 선택: 상위 신청서 상태는 유지되므로 UI도 실제 상태로 되돌림
          setTempStatus(application.status as ActualApplicationStatus);
        }
      } else {
        await onStatusChange(tempStatus);
      }
      
      // 2. 알림톡 전송 (체크된 경우)
      let alertSent = false;
      if (withAlert && sendAlert && alertPreviewMessage) {
        const templateType = STATUS_TO_ALERT_TEMPLATE[tempStatus];
        if (templateType) {
          try {
            await sendAlertMessage(templateType);
            alertSent = true;
          } catch (alertError) {
            console.error('알림톡 전송 실패:', alertError);
            // 알림톡 실패해도 상태 변경은 성공했으므로 계속 진행
          }
        }
      }

      // 3. 상태 초기화
      setIsStatusChanged(false);
      setSendAlert(false);
      setAlertPreviewMessage('');
      
      // 4. 후처리 콜백 (스낵바/리프레시 등)
      if (onAfterSave) {
        onAfterSave();
      }
      
      console.log(`✅ 상태 변경 완료: ${tempStatus}${alertSent ? ' (알림톡 전송 완료)' : ''}`);
    } catch (error) {
      console.error('상태 변경 실패:', error);
    } finally {
      setIsStatusSaving(false);
    }
  }, [onStatusChange, isStatusChanged, tempStatus, sendAlert, alertPreviewMessage, sendAlertMessage, onAfterSave]);

  // 🔄 상태 리셋 핸들러
  const handleStatusReset = useCallback(() => {
    setTempStatus(application.status as ActualApplicationStatus);
    setIsStatusChanged(false);
    setSendAlert(true);
    setAlertPreviewMessage('');
  }, [application.status]);

  // 🔔 알림톡 체크박스 변경 핸들러
  const handleAlertCheckChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSendAlert(event.target.checked);
  }, []);

  // 👁️ 미리보기 다이얼로그 열기
  const handlePreviewOpen = useCallback(() => {
    setAlertPreviewOpen(true);
  }, []);

  // 👁️ 미리보기 다이얼로그 닫기
  const handlePreviewClose = useCallback(() => {
    setAlertPreviewOpen(false);
    setIsInfoRequestMode(false); // 모드 리셋
  }, []);

  // 🆕 정보입력요청 선택 핸들러 (상태 변경 없이 알림톡 발송용)
  const handleInfoRequestSelect = useCallback(() => {
    // 정보입력요청 미리보기 메시지 생성
    const template = biztalkTemplates['정보입력요청'];
    if (template) {
      const today = new Date();
      const applyDate = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
      const preview = template.getMessage({
        userName: application.user_name,
        deviceName: formatDeviceNames(application.devices),
        applyDate: applyDate,
      });
      setAlertPreviewMessage(preview);
    }
    setIsInfoRequestMode(true);
    setAlertPreviewOpen(true);
  }, [application]);

  // 🆕 정보입력요청 실행 (상태 변경 없이 알림톡만 발송)
  const executeInfoRequest = useCallback(async () => {
    setIsStatusSaving(true);
    try {
      const today = new Date();
      const applyDate = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
      
      const payload = {
        application_id: application.id,
        user_name: application.user_name,
        phone_number: application.phone_number,
        device_name: formatDeviceNames(application.devices),
        apply_date: applyDate,
        event: '정보입력요청',
        token: application.info_token_id || '', // 기존 토큰 또는 빈 문자열 (API에서 생성)
      };

      console.log('📤 [정보입력요청] 발송 시도:', payload);

      const response = await fetch(`${API_BASE_URL}/api/alerts/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      console.log('✅ [정보입력요청] 발송 성공');
      
      // 다이얼로그 닫기 및 모드 리셋
      setAlertPreviewOpen(false);
      setIsInfoRequestMode(false);
      setAlertPreviewMessage('');
      
      // 후처리 콜백
      if (onAfterSave) {
        onAfterSave();
      }
    } catch (error) {
      console.error('❌ [정보입력요청] 발송 실패:', error);
      throw error;
    } finally {
      setIsStatusSaving(false);
    }
  }, [application, onAfterSave]);

  return {
    tempStatus,
    isStatusChanged,
    isStatusSaving,
    sendAlert,
    alertPreviewOpen,
    alertPreviewMessage,
    perDevicePreviews,
    perDevicePreviewsSelected,
    canSendAlert,
    setTempStatus,
    setIsStatusChanged,
    setIsStatusSaving,
    setSendAlert,
    setAlertPreviewOpen,
    setAlertPreviewMessage,
    selectedDeviceIds,
    toggleDeviceSelection: (deviceId: string) => {
      setSelectedDeviceIds(prev => prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]);
    },
    toggleAllSelection: (checked: boolean) => {
      setSelectedDeviceIds(checked ? (application.devices || []).map(d => d.id) : []);
    },
    handleStatusChange,
    handleStatusSave,
    handleStatusReset,
    handleAlertCheckChange,
    handlePreviewOpen,
    handlePreviewClose,
    executeStatusSave,
    // 🆕 정보입력요청 관련
    isInfoRequestMode,
    handleInfoRequestSelect,
    executeInfoRequest,
  };
};